import React from 'react';
import { Package, Tag, PlusCircle } from 'lucide-react';
import ShimmerImage from '../../components/ShimmerImage';
import { billingApi } from '../../services/billingApi';
import { dealsApi } from '../../services/dealsApi';
import { api } from '../../services/apiService';
import { useDataVersion } from '../../services/dataChangeBus';

/**
 * WHAT WAS ORDERED, AND WHAT IT COST — the `placed` stage.
 *
 * The list was device pictures with "rental · $14.99" under each and no total
 * anywhere: an operator could see the parts and not what the customer paid.
 * Worse, a device the offer discounted showed its DEAL price with nothing
 * saying it had been discounted, so the one fact that explains an unusual
 * figure was the one missing.
 *
 * Three records, the same three the rest of this screen reconciles against:
 * the order's own lines, the completion's `oneTimeLines` (deposits and down
 * payments, which are not order lines and are the usual reason the money taken
 * exceeds the order), and the PINNED deal version's `deviceTerms` — the offer
 * as contracted, never the live deal, which may since have been edited.
 */

const money = (cents?: number) =>
  typeof cents === 'number' ? `$${(cents / 100).toFixed(2)}` : '—';

interface OrderItem {
  modelId?: string;
  bundleId?: string;
  skuId?: string;
  quantity?: number;
  amountCents?: number;
  saleMode?: string;
  termMonths?: number;
}

const OrderPlacedPanel: React.FC<{
  tenantId: string;
  items: OrderItem[];
  models: Array<{ modelId: string; modelName?: string; manufacturer?: string; media?: { heroImageUrl?: string } }>;
  /**
   * THE OFFER THIS ORDER WAS PLACED UNDER — the order's own snapshot, frozen at
   * checkout, exactly as `PlanPanel` beside it already uses.
   *
   * An order is a historical record and must be read against the offer it was
   * bought on, never the plan the tenant happens to be on today. This panel
   * resolved the deal from `tenant.dealId`, so a customer who later switched
   * plans had every device on a past order re-judged against the new offer.
   */
  orderDealCode?: string;
}> = ({ tenantId, items, models, orderDealCode }) => {
  const [oneTimeLines, setOneTimeLines] = React.useState<Array<{ skuId: string; quantity: number; amountCents: number }>>([]);
  const [deviceTerms, setDeviceTerms] = React.useState<Record<string, any>>({});
  /*
    THE DEAL'S OWN DEVICE SKUS — what the offer covers.

    Ruling 2026-09-06: a deal's price holds for the deal's own lines, and
    anything bought on top prices at list. So "in the deal" and "add-on" are
    different commercial objects that bill differently, and a screen that shows
    them as one undifferentiated list gives an operator no way to tell an
    unusual figure from a wrong one.

    `null` IS A THIRD ANSWER, and the important one: we could not establish what
    this offer covers. It started as an empty Set, which reads as "the offer
    covers nothing" — so a deal that failed to resolve for ANY reason (a tenant
    pointing at the wrong deal, a withdrawn offer, a 404) printed ADD-ON against
    every device on the order, with total confidence, from no data at all. An
    unknown offer now says nothing rather than something false.
  */
  const [dealDeviceSkuIds, setDealDeviceSkuIds] = React.useState<Set<string> | null>(null);
  const [skuNames, setSkuNames] = React.useState<Record<string, string>>({});
  const [paidCents, setPaidCents] = React.useState<number | null>(null);
  const dataVersion = useDataVersion();

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const [sold, tenant, skus, invoices] = await Promise.all([
        billingApi.getOnboardingResult(tenantId).catch(() => null),
        api.superAdmin.getTenant(tenantId).catch(() => null),
        billingApi.listSkus().catch(() => null),
        api.superAdmin.getTenantInvoices(tenantId, 20).catch(() => null),
      ]);
      if (cancelled) return;

      setOneTimeLines(((sold as any)?.result?.locations || []).flatMap((l: any) => l.oneTimeLines || []));
      setSkuNames(Object.fromEntries(((skus as any)?.items || []).map((s: any) => [s.skuId, s.name])));
      // EVERY paid invoice on the customer — the subscription's first month and
      // the one-time one alike. This stage is asked "did the money land", and
      // the answer is all of it or none of it.
      setPaidCents(((invoices as any)?.invoices || [])
        .filter((i: any) => i.status === 'paid')
        .reduce((n: number, i: any) => n + (i.amountPaid || 0), 0));

      /*
        WHICH OFFER TO JUDGE THIS ORDER AGAINST — the order's own code first.

        It was `tenant.dealId` alone, which is the plan the customer is on NOW.
        Worse, `POST /onboarding/deal` wrote `dealCode` and `dealVersionNo` on a
        switch and left `dealId` untouched (fixed 2026-09-10 in billing-service),
        so that id could name an offer the tenant had already left — and the
        version number beside it belonged to a different deal again.

        The code is resolved to a deal through the same public lookup `PlanPanel`
        uses, which overlays the deal's ACTIVE published version, so a deal whose
        hardware lives only on its version resolves to that hardware.

        The tenant's PINNED version is preferred over the active one, but only
        when the pin is demonstrably about this same deal — ids compared, never
        codes, because the id/code disagreement above is exactly the fault that
        produced this bug.
      */
      const byCode: any = orderDealCode
        ? await api.deals.getDealByCode(orderDealCode).catch(() => null)
        : null;
      const orderDeal = byCode?.deal;
      const tenantDealId = (tenant as any)?.dealId;
      const pinnedNo = (tenant as any)?.dealVersionNo;
      // A legacy order with no code of its own still falls back to the tenant's
      // pin — it is the only record such an order has.
      const dealId = orderDeal?.id || (orderDealCode ? null : tenantDealId);
      const samePin = dealId && tenantDealId === dealId && typeof pinnedNo === 'number';

      let devices: string[] | null = null;
      let terms: Record<string, any> | null = null;

      if (samePin) {
        const v: any = await dealsApi.getVersion(dealId, pinnedNo).catch(() => null);
        const version = v?.version || v;
        if (version) {
          // Both spellings of "the hardware this offer includes" — a published
          // version may carry either (`devicePackageIds` is the newer name and
          // the only one some versions store), and reading just one of them
          // called every device on a fully-bundled offer an add-on.
          devices = version.devicePackageIds ?? version.deviceSkuIds ?? null;
          terms = version.deviceTerms || null;
        }
      }
      if (devices === null && orderDeal) {
        // `getDealByCode` has already overlaid the active version.
        devices = orderDeal.devicePackageIds ?? orderDeal.deviceSkuIds ?? null;
        terms = terms ?? orderDeal.deviceTerms ?? null;
      }

      if (!cancelled) {
        setDeviceTerms(terms || {});
        setDealDeviceSkuIds(devices ? new Set<string>(devices) : null);
      }
    })();
    return () => { cancelled = true; };
  // dataVersion: re-read in place after a save anywhere on the order screen.
  }, [tenantId, orderDealCode, dataVersion]);

  const qtyOf = (i: OrderItem) => i.quantity || 1;

  /*
    EACH DEVICE'S OWN ONE-TIME MONEY, ON ITS OWN ROW.

    The deposits and down payments were a separate list keyed by raw sku —
    "dev_mdl_partner_tech_e5_dual_screen_finance_36m_sve0_down · down payment" —
    under a list of devices that already had pictures and names. The reader had
    to match an id to a photograph to learn which $150.00 belonged to which
    terminal. A device's deposit is a fact ABOUT that device, so it belongs on
    its line.

    Matched on the device's own skuId, allowing for the `_deposit` and `_down`
    suffixes the completion mints its synthetic one-time lines under. Anything
    that matches nothing is still listed below rather than dropped: an unmatched
    charge is money the customer paid, and hiding it because a join failed is the
    one outcome this panel must never produce.
  */
  const oneTimeFor = (skuId?: string) =>
    (skuId ? oneTimeLines.filter((l) => l.skuId === skuId || l.skuId.startsWith(`${skuId}_`)) : []);
  const matched = new Set(
    items.flatMap((i) => oneTimeFor(i.skuId).map((l) => l.skuId)),
  );
  const unmatchedOneTime = oneTimeLines.filter((l) => !matched.has(l.skuId));
  // Recurring hardware — a rental or an instalment. An outright purchase is not
  // a monthly commitment and belongs with the one-time money below.
  const monthlyCents = items
    .filter((i) => i.saleMode && i.saleMode !== 'outright' && i.saleMode !== 'included')
    .reduce((n, i) => n + (i.amountCents || 0) * qtyOf(i), 0);
  const oneTimeCents = oneTimeLines.reduce((n, l) => n + l.amountCents * (l.quantity || 1), 0);

  /**
   * IS THIS DEVICE ON THE OFFER — `null` while the offer itself is unknown.
   *
   * The three answers are kept apart deliberately: yes, no, and "we could not
   * read the offer". Only the first two may be printed.
   */
  const onOffer = (skuId?: string): boolean | null =>
    dealDeviceSkuIds === null ? null : Boolean(skuId && dealDeviceSkuIds.has(skuId));

  /** The same split the rows carry, totalled — see `dealDeviceSkuIds`. */
  const recurring = items.filter((i) => i.saleMode && i.saleMode !== 'outright' && i.saleMode !== 'included');
  const inDeal = recurring.filter((i) => onOffer(i.skuId) === true);
  const addOns = recurring.filter((i) => onOffer(i.skuId) === false);
  const sumOf = (rows: OrderItem[]) => rows.reduce((n, i) => n + (i.amountCents || 0) * qtyOf(i), 0);
  const dealMonthlyCents = sumOf(inDeal);
  const addOnMonthlyCents = sumOf(addOns);
  const dealDeviceCount = inDeal.reduce((n, i) => n + qtyOf(i), 0);
  const addOnDeviceCount = addOns.reduce((n, i) => n + qtyOf(i), 0);

  /** A down payment names itself; everything else one-time is a deposit. */
  const isDownPayment = (skuId: string) => /down|dp$/i.test(skuId);

  return (
    <div className="space-y-2">
      {items.map((it, i) => {
        const m = models.find((x) => x.modelId === it.modelId);
        // The offer on THIS device, if the deal named one.
        const term = it.skuId ? deviceTerms[it.skuId] : undefined;
        const listCents = term?.listMonthlyCents ?? term?.listCents;
        const discounted = typeof listCents === 'number'
          && typeof it.amountCents === 'number'
          && listCents > it.amountCents;
        return (
          <div key={i} className="flex items-center gap-3 px-2.5 py-2 rounded-xl border border-stone-200 bg-white">
            {/* The device, as a picture. Reading "TC-TOUCH-K10-WHITE-DUAL-TOUCH"
                and knowing what is in the box are different things. */}
            <ShimmerImage
              src={m?.media?.heroImageUrl}
              alt={m?.modelName || it.modelId || 'device'}
              width={44}
              height={44}
              objectFit="contain"
              fallbackIcon={<Package size={18} className="text-gray-400" />}
              fallbackBgColor="bg-gray-100"
              variant="light"
            />
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-semibold text-stone-900 truncate">
                {qtyOf(it)} × {m ? `${m.manufacturer} ${m.modelName}` : (it.modelId || it.bundleId || it.skuId)}
              </p>
              <p className="text-[11px] text-stone-500 mt-0.5">
                {it.saleMode}
                {it.termMonths ? ` · ${it.termMonths} months` : ''}
                {/* The offer, when the deal moved this device off list. Amber,
                    struck price first, so an unusual figure explains itself. */}
                {discounted && (
                  <>
                    {' · '}
                    <span className="line-through text-stone-400">{money(listCents * qtyOf(it))}</span>
                    <span className="text-amber-700 font-semibold"> {money((it.amountCents || 0) * qtyOf(it))}</span>
                    <span className="text-amber-700"> offer</span>
                  </>
                )}
                {!discounted && it.amountCents ? ` · ${money(it.amountCents * qtyOf(it))}` : ''}
              </p>
              {/* This device's own deposit and down payment, on this device's
                  row — see `oneTimeFor`. */}
              {oneTimeFor(it.skuId).map((l) => (
                <p key={l.skuId} className="text-[11px] text-stone-500 mt-0.5 flex items-baseline gap-1.5">
                  <span className={isDownPayment(l.skuId) ? 'text-stone-600 font-semibold' : 'text-stone-500'}>
                    {isDownPayment(l.skuId) ? 'Down payment' : 'Refundable deposit'}
                  </span>
                  <span className="tabular-nums">{money(l.amountCents * (l.quantity || 1))}</span>
                  <span className="text-stone-400">· charged once at checkout</span>
                </p>
              ))}
            </div>
            {/* IN THE DEAL, or bought on top of it — see the ruling note on
                `inDeal`. Stated per device, because the two price differently
                and an operator checking an unusual figure needs to know which
                kind of line they are reading. */}
            {it.skuId && onOffer(it.skuId) !== null && (
              <span className={`shrink-0 self-start inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${
                onOffer(it.skuId)
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
                {/* A TAG for what the offer covers, a PLUS for what was added
                    on top — the shape says which before the word is read. */}
                {onOffer(it.skuId)
                  ? <Tag size={10} strokeWidth={3} />
                  : <PlusCircle size={10} strokeWidth={3} />}
                {onOffer(it.skuId) ? 'in deal' : 'add-on'}
              </span>
            )}
          </div>
        );
      })}

      {/*
        THE MONEY, UNDER THE THINGS IT PAID FOR.

        Monthly and one-time are DIFFERENT UNITS and are never summed: an
        instalment rate added to a deposit is a figure nobody is charged. Each
        is named, and what actually landed is stated beside them.
      */}
      <div className="rounded-xl border border-stone-200 bg-stone-50 px-3 py-2.5 space-y-1.5">
        {/* SPLIT BY WHAT THE DEAL COVERS. The two price differently, so a
            single "hardware, monthly" hides the question an operator is
            actually asking about an unexpected figure. */}
        {dealMonthlyCents > 0 && (
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[12px] text-stone-600">
              In the deal, monthly
              <span className="text-stone-400"> · {dealDeviceCount} device{dealDeviceCount === 1 ? '' : 's'}</span>
            </span>
            <span className="text-[12.5px] font-semibold text-stone-900 tabular-nums">{money(dealMonthlyCents)}</span>
          </div>
        )}
        {addOnMonthlyCents > 0 && (
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[12px] text-stone-600">
              Add-ons at list, monthly
              <span className="text-stone-400"> · {addOnDeviceCount} device{addOnDeviceCount === 1 ? '' : 's'}</span>
            </span>
            <span className="text-[12.5px] font-semibold text-stone-900 tabular-nums">{money(addOnMonthlyCents)}</span>
          </div>
        )}
        <div className="flex items-baseline justify-between gap-3 pt-1.5 border-t border-stone-200">
          <span className="text-[12px] font-semibold text-stone-700">Hardware, monthly</span>
          <span className="text-[12.5px] font-bold text-stone-900 tabular-nums">{money(monthlyCents)}</span>
        </div>

        {/* Only what could NOT be attributed to a device above — see
            `oneTimeFor`. Never dropped: an unmatched charge is money the
            customer paid, and hiding it because a join failed is the one
            outcome this panel must not produce. */}
        {unmatchedOneTime.map((l, i) => (
          <div key={`${l.skuId}_${i}`} className="flex items-baseline justify-between gap-3">
            <span className="text-[11.5px] text-stone-500 truncate">
              {skuNames[l.skuId.replace(/_(deposit|down)$/, '')] || l.skuId}
              <span className="text-stone-400">
                {' · '}{isDownPayment(l.skuId) ? 'down payment' : 'refundable deposit'}
              </span>
            </span>
            <span className="text-[12px] text-stone-700 tabular-nums shrink-0">
              {money(l.amountCents * (l.quantity || 1))}
            </span>
          </div>
        ))}

        {oneTimeCents > 0 && (
          <div className="flex items-baseline justify-between gap-3 pt-1.5 border-t border-stone-200">
            <span className="text-[12px] font-semibold text-stone-700">Deposits &amp; down payments</span>
            <span className="text-[12.5px] font-bold text-stone-900 tabular-nums">{money(oneTimeCents)}</span>
          </div>
        )}

        <div className="flex items-baseline justify-between gap-3 pt-1.5 border-t border-stone-200">
          <span className="text-[12px] font-semibold text-stone-700">
            Paid to date
            <span className="text-stone-400 font-normal"> · every invoice on this customer</span>
          </span>
          <span className={`text-[13px] font-extrabold tabular-nums ${
            paidCents ? 'text-emerald-700' : 'text-stone-400'
          }`}>
            {paidCents === null ? '…' : money(paidCents)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default OrderPlacedPanel;
